import { Injectable } from '@angular/core';
import { ChatHttpService } from '@app/core/async-services/http/versioned';
import { MessageWithImages } from '@app/core/async-services/http/versioned/interfaces/announcement.interface';
import { API, User } from '@app/core/models';
import { State } from '@app/store';
import { selectUser } from '@app/store/user/user.selectors';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  first,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { selectRecipients } from './message.selectors';
import * as MessageActions from './message.actions';
import { firstTruthy } from '@app/core/utils/rxjs.util';

@Injectable()
export class Effects {
  loadContacts$ = createEffect(() =>
    this._actions.pipe(
      ofType(MessageActions.loadContacts),
      mergeMap(() =>
        this._chatHttpService.getChatContacts().pipe(
          map((response) => MessageActions.loadContactsSuccess({ response })),
          catchError((response: API.ChatContactsSuccess) =>
            of(MessageActions.loadContactsFailure({ response })),
          ),
        ),
      ),
    ),
  );

  getChatChannelForUsers$ = createEffect(() =>
    this._actions.pipe(
      ofType(MessageActions.selectPeopleNext),
      withLatestFrom(this._store.select('messageState')),
      switchMap(([_, { recipients }]) =>
        this._chatHttpService.getChatChannelForUsers(recipients).pipe(
          map((response) =>
            response.payload.channel
              ? MessageActions.getChatChannelForUsersSuccessChannelExists({
                  channel: response.payload.channel,
                })
              : MessageActions.getChatChannelForUsersSuccessChannelDoesNotExist(),
          ),
          catchError((response) =>
            of(MessageActions.getChatChannelForUsersFailure(response)),
          ),
        ),
      ),
    ),
  );

  routeToViewChatPage$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(
          MessageActions.getChatChannelForUsersSuccessChannelExists,
          MessageActions.sendFirstMessageSuccess,
        ),
        switchMap(({ channel }) =>
          this._navController.navigateRoot(
            '/app/communication/messages/view-chat',
            {
              queryParams: { ch: channel },
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  routeToNewChatPage$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(MessageActions.getChatChannelForUsersSuccessChannelDoesNotExist),
        switchMap(() =>
          this._navController.navigateRoot(
            '/app/communication/messages/new-chat',
          ),
        ),
      ),
    { dispatch: false },
  );

  routeToSelectPeoplePage$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(MessageActions.addPeopleToChat),
        switchMap(() =>
          this._navController.navigateRoot(
            '/app/communication/messages/select-people',
          ),
        ),
      ),
    { dispatch: false },
  );

  createChatChannelForUsers$ = createEffect(() =>
    this._actions.pipe(
      ofType(MessageActions.sendFirstMessage),
      switchMap(({ message, secureUrls }) =>
        this._store.select(selectRecipients).pipe(
          first(),
          switchMap((recipients) =>
            this._chatHttpService.createChatChannelForUsers(recipients).pipe(
              map(({ payload: { channel } }) =>
                MessageActions.createChatChannelForUsersSuccess({
                  channel,
                  message,
                  secureUrls,
                }),
              ),
              catchError(() => EMPTY),
            ),
          ),
        ),
      ),
    ),
  );

  sendFirstMessage$ = createEffect(() =>
    this._actions.pipe(
      ofType(MessageActions.createChatChannelForUsersSuccess),
      switchMap((action) =>
        this._store.select(selectUser).pipe(
          firstTruthy(),
          map((user) => ({ ...action, user })),
        ),
      ),
      map(({ channel, message, secureUrls, user }) => {
        const publishParams: MessageWithImages = {
          channel,
          message,
          meta: {
            author: {
              firstName: user.name.first,
              lastName: user.name.last,
              userName: user.userName,
              userID: user.WebUserId,
              userType: User.Type.MANAGER,
            },
            guid: uuidv4(),
          },
          images: secureUrls,
        };
        return publishParams;
      }),
      switchMap((publishParams) =>
        this._chatHttpService.sendMessage(publishParams).pipe(
          map(() =>
            MessageActions.sendFirstMessageSuccess({
              channel: publishParams.channel,
            }),
          ),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  constructor(
    private _actions: Actions,
    private _chatHttpService: ChatHttpService,
    private _store: Store<State>,
    private _navController: NavController,
  ) {}
}
